'use strict';

const _ = require('underscore');
const Moment = require('moment');
const Backbone = require('backbone');
const Actions = require('common/domain/action/index');
const Dispatcher = require('common/domain/repository/dispatcher');
const Logger = require('common/util/logger');
const RelationTypeRepository = require('system/domain/repository/relation-type');
const NodeModel = require('system/domain/entity/node');
const LinkModel = require('system/domain/entity/link');
const NodeCollection = require('system/domain/collection/node');
const LinkCollection = require('system/domain/collection/link');

const defaultValueTypes = [
    {
        id: 'String',
        type: 'ValueType',
        name: 'String',
        values: [
            {
                id: 'String',
                type: 'String'
            }
        ],
        validate: function(value) {
            return _.isString(value);
        }
    },
    {
        id: 'Integer',
        type: 'ValueType',
        name: 'Integer',
        values: [
            {
                id: 'Integer',
                type: 'Integer'
            }
        ],
        validate: function(value) {
            return !_.isNaN(value) && _.isNumber(value) &&
                parseInt(value, 10) == value;
        }
    },
    {
        id: 'Real',
        type: 'ValueType',
        name: 'Real',
        values: [
            {
                id: 'Real',
                type: 'Real'
            }
        ],
        validate: function(value) {
            return !_.isNaN(value) && _.isNumber(value);
        }
    },
    {
        id: 'Boolean',
        type: 'ValueType',
        name: 'Boolean',
        values: [
            {
                id: 'Boolean',
                type: 'Boolean'
            }
        ],
        validate: function(value) {
            return _.isBoolean(value);
        }
    }
];

const ElementStore = Backbone.Model.extend({
    initialize: function() {
        _.bindAll(this, 'dispatch');
        this._contextNodes = {};
        this._contextLinks = {};
        this._contextValueTypes = {};
        Dispatcher.register(this.dispatch);
    },

    _nodes: function(context) {
        let key = context.projectId + ':' + context.dt;
        let nodes = this._contextNodes[key];
        if(nodes) {
            return nodes;
        }
        this._contextNodes[key] = new NodeCollection();
        return this._contextNodes[key];
    },

    _valueTypes: function(context) {
        let key = context.projectId;
        let valueTypes = this._contextValueTypes[key];
        if(valueTypes) {
            return valueTypes;
        }
        valueTypes = new Backbone.Collection(
            defaultValueTypes
        );
        valueTypes.add(this._nodes(context).filter(function(n) {
            return n.get('type') == 'ValueType';
        }));
        this._nodes(context).on('add', function(n) {
            if(n.get('type') == 'ValueType') {
                valueTypes.add(n);
            }
        });
        this._nodes(context).on('remove', function(n) {
            if(n.get('type') == 'ValueType') {
                valueTypes.remove(n);
            }
        });
        this._nodes(context).on('change:type', function(n) {
            if(n.get('type') == 'ValueType') {
                valueTypes.add(n);
            } else if(n.previous('type') == 'ValueType') {
                valueTypes.remove(n);
            }
        });
        this._contextValueTypes[key] = valueTypes;
        return valueTypes;
    },

    _links: function(context) {
        let key = context.projectId + ':' + context.dt;
        let links = this._contextLinks[key];
        if(links) {
            return links;
        }
        this._contextLinks[key] = new LinkCollection();
        return this._contextLinks[key];
    },

    clearAll: function() {
        this._contextNodes = {};
        this._contextLinks = {};
        this._contextValueTypes = {};
    },

    getNodes: function(params) {
        return this._nodes(params.context);
    },

    getValueTypes: function(params) {
        return this._valueTypes(params.context);
    },

    getLinks: function(params) {
        return this._links(params.context);
    },

    dispatch: function(action) {
        switch(action.type) {
            case Actions.CREATE_NODE:
                this._createNode(action);
                break;
            case Actions.UPDATE_NODE:
                this._updateNode(action);
                break;
            case Actions.DELETE_NODE:
                this._deleteNode(action);
                break;
            case Actions.MERGE_NODE:
                this._mergeNode(action);
                break;
            case Actions.UPDATE_NODE_SYNC_STATE:
                this._updateNodeSyncState(action);
                break;
            case Actions.UPDATE_NODE_EDIT_STATE:
                this._updateNodeEditState(action);
                break;
            case Actions.ADD_TAG_TO_NODE:
                this._addTagToNode(action);
                break;
            case Actions.REMOVE_TAG_FROM_NODE:
                this._removeTagFromNode(action);
                break;
            case Actions.ADD_OWNER_TO_NODE:
                this._addOwnerToNode(action);
                break;
            case Actions.REMOVE_OWNER_FROM_NODE:
                this._removeOwnerFromNode(action);
                break;
            case Actions.CREATE_LINK:
                this._createLink(action);
                break;
            case Actions.UPDATE_LINK:
                this._updateLink(action);
                break;
            case Actions.DELETE_LINK:
                this._deleteLink(action);
                break;
            case Actions.MERGE_LINK:
                this._mergeLink(action);
                break;
            case Actions.UPDATE_LINK_SYNC_STATE:
                this._updateLinkSyncState(action);
                break;
            case Actions.UPDATE_LINK_EDIT_STATE:
                this._updateLinkEditState(action);
                break;
        }
    },

    _createNode: function(action) {
        let _nodes = this._nodes(action.context);
        let nodes = _.isArray(action.node) ?
            action.node : [action.node];

        nodes = _.chain(nodes)
            .filter(function(node){
                if(_nodes.get(node.id)) {
                    Logger.debug(
                        `${node.id} already exists.`);
                    return false;
                }
                return true;
            })
            .map(function(node){
                return new NodeModel(node);
            })
            .value();

        if(action.options && action.options.fetched) {
            _nodes.add(nodes, {merge: true, silent: true});
            _nodes.trigger('reset');
        } else {
            _nodes.add(nodes, {merge: true});
        }
    },

    _updateNode: function(action) {
        let _nodes = this._nodes(action.context);
        if(!action.node) {
            return;
        }

        let nodes = _.isArray(action.node) ?
            action.node : [action.node];
        _.each(nodes, function(j) {
            let node = _nodes.get(j.id);
            if(!node) {
                Logger.debug(
                    `${node.id} does not exist.`);
            } else {
                node.set(j);
            }
        });
    },

    _mergeNode: function(action) {
        let _nodes = this._nodes(action.context);
        if(!action.node) {
            return;
        }

        let nodes = _.isArray(action.node) ?
            action.node : [action.node];
        _.each(nodes, function(j) {
            let node = _nodes.get(j.id);
            if(!node) {
                this._createNode({
                    context: action.context,
                    node: j
                });
                return;
            }

            let state = node.syncState;
            if(state.isConflict() || state.isSyncing()) {
                return;
            }
            if(state.isUnsync() &&
                Moment(node.get('updated_at')).valueOf() <
                Moment(j.updated_at).valueOf()){
                state.setConflict();
                return;
            }
            if(Moment(node.get('updated_at')).valueOf() ==
                Moment(j.updated_at).valueOf()){
                return;
            }

            if(j.deleted) {
                this._deleteNode({
                    context: action.context,
                    node: j
                });
            }
            this._updateNode({
                context: action.context,
                node: j
            });
        }, this);
    },

    _deleteNode: function(action) {
        let _nodes = this._nodes(action.context);
        if(!action.node) {
            return;
        }

        let nodes = _.isArray(action.node) ?
            action.node : [action.node];
        _.each(nodes, function(j) {
            let node = _nodes.get(j.id);
            if(!node) {
                Logger.debug(
                    `${node.id} does not exist.`);
            } else {
                node.set({deleted: true});
            }
        });
    },

    _updateNodeSyncState: function(action) {
        let _nodes = this._nodes(action.context);
        let nodes = _.isArray(action.node) ?
            action.node : [action.node];

        _.each(nodes, function(j) {
            let node = _nodes.get(j.id);
            if(node) {
                node.syncState.setState(action.state);
            }
        });
    },

    _updateNodeEditState: function(action) {
        let _nodes = this._nodes(action.context);
        let nodes = _.isArray(action.node) ?
            action.node : [action.node];

        _.each(nodes, function(j) {
            let node = _nodes.get(j.id);
            if(node) {
                switch(action.state) {
                    case 'start':
                        node.startEdit();
                        break;
                    case 'end':
                        node.endEdit();
                        break;
                    case 'cancel':
                        node.cancelEdit();
                        break;
                }
            }
        });
    },

    _addTagToNode: function(action) {
        let _nodes = this._nodes(action.context);
        let nodes = _.isArray(action.node) ?
            action.node : [action.node];

        _.each(nodes, function(j) {
            let node = _nodes.get(j.id);
            if(node) {
                node.addTag(action.tag.id);
            }
        });
    },

    _removeTagFromNode: function(action) {
        let _nodes = this._nodes(action.context);
        let nodes = _.isArray(action.node) ?
            action.node : [action.node];

        _.each(nodes, function(j) {
            let node = _nodes.get(j.id);
            if(node) {
                node.removeTag(action.tag.id);
            }
        });
    },

    _addOwnerToNode: function(action) {
        let _nodes = this._nodes(action.context);
        let nodes = _.isArray(action.node) ?
            action.node : [action.node];

        _.each(nodes, function(j) {
            let node = _nodes.get(j.id);
            if(node) {
                node.addOwner(action.user.id);
            }
        });
    },

    _removeOwnerFromNode: function(action) {
        let _nodes = this._nodes(action.context);
        let nodes = _.isArray(action.node) ?
            action.node : [action.node];

        _.each(nodes, function(j) {
            let node = _nodes.get(j.id);
            if(node) {
                node.removeOwner(action.user.id);
            }
        });
    },


    _createLink: function(action) {
        let _nodes = this._nodes(action.context);
        let _links = this._links(action.context);
        let links = _.isArray(action.link) ?
            action.link : [action.link];

        links = _.chain(links)
            .filter(function(link){
                link['source'] = _nodes.get(link.source);
                link['target'] = _nodes.get(link.target);

                // タイミング次第でリンク先の Node が消えている可能性がある
                // その場合は何もせずに return する
                if(!link.source || !link.target) {
                    return false;
                }

                if(_links.get(link.id)) {
                    Logger.debug(
                        `${link.id} already exists.`);
                    return false;
                }
                return true;
            })
            .map(function(link){
                let linkType = RelationTypeRepository.getRelationTypes().get(link.type);
                // まだ RelationType がロードされていない可能性を考慮
                if(linkType) {
                    let m = linkType.get('multiplicity');
                    if(m && !link.multiplicity) {
                        _.extend(link, {multiplicity: m});
                    }
                    if(!m) {
                        delete link.multiplicity;
                    }
                }
                return new LinkModel(link);
            })
            .value();

        if(action.options && action.options.fetched) {
            _links.add(links, {merge: true, silent: true});
            _links.trigger('reset');
        } else {
            _links.add(links, {merge: true});
        }
    },

    _updateLink: function(action) {
        let _nodes = this._nodes(action.context);
        let _links = this._links(action.context);
        let links = _.isArray(action.link) ?
            action.link : [action.link];

        _.each(links, function(link){
            if(link.source) {
                link.source = _nodes.get(link.source);
                if(!link.source) {
                    return;
                }
            }
            if(link.target) {
                link.target = _nodes.get(link.target);
                if(!link.target) {
                    return;
                }
            }

            if(!_links.get(link.id)) {
                Logger.debug(
                    `${link.id} does not exist.`);
                return;
            }

            let l = _links.get(link.id);
            let type = link.type ? link.type : l.get('type');
            let linkType = RelationTypeRepository.getRelationTypes().get(type);
            let m = linkType.get('multiplicity');
            if(m && !l.get('multiplicity') && !link.multiplicity) {
                _.extend(link, {multiplicity: m});
            }

            l.set(link);
            if(!m) {
                l.unset('multiplicity');
            }
        });
    },

    _mergeLink: function(action) {
        let _links = this._links(action.context);
        if(!action.link) {
            return;
        }

        let links = _.isArray(action.link) ?
            action.link : [action.link];
        _.each(links, function(j) {
            let link = _links.get(j.id);
            if(!link) {
                this._createLink({
                    context: action.context,
                    link: j
                });
                return;
            }

            let state = link.syncState;
            if(state.isConflict() || state.isSyncing()) {
                return;
            }
            if(state.isUnsync() &&
                Moment(link.get('updated_at')).valueOf() <
                Moment(j.updated_at).valueOf()){
                state.setConflict();
                return;
            }
            if(Moment(link.get('updated_at')).valueOf() ==
                Moment(j.updated_at).valueOf()){
                return;
            }

            if(j.deleted) {
                this._deleteLink({
                    context: action.context,
                    link: j
                });
            }
            this._updateLink({
                context: action.context,
                link: j
            });
        }, this);
    },

    _deleteLink: function(action) {
        let _links = this._links(action.context);
        let links = _.isArray(action.link) ?
            action.link : [action.link];

        _.each(links, function(link){
            if(!_links.get(link.id)) {
                Logger.debug(
                    `${link.id} does not exist.`);
                return;
            }

            _links.get(link.id).set({deleted: true});
        });
    },

    _updateLinkSyncState: function(action) {
        let _links = this._links(action.context);
        let links = _.isArray(action.link) ?
            action.link : [action.link];

        _.each(links, function(j) {
            let link = _links.get(j.id);
            if(link) {
                link.syncState.setState(action.state);
            }
        });
    },

    _updateLinkEditState: function(action) {
        let _links = this._links(action.context);
        let links = _.isArray(action.link) ?
            action.link : [action.link];

        _.each(links, function(j) {
            let link = _links.get(j.id);
            if(link) {
                switch(action.state) {
                    case 'start':
                        link.startEdit();
                        break;
                    case 'end':
                        link.endEdit();
                        break;
                    case 'cancel':
                        link.cancelEdit();
                        break;
                }
            }
        });
    }
});

module.exports = new ElementStore();



// WEBPACK FOOTER //
// ./system/domain/repository/element.js